import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useCookieConsent } from '@/components/custom/cookie-consent';
import { Link } from 'react-router';

import { TiktokIcon } from '@/components/custom/tiktok-icon';
import { isLive, stateName, statePath } from '~/lib/states';
import { useLayoutData } from '~/lib/layout-data';

const SOCIAL_LINKS = [
  { Icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/bh_field/' },
  { Icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/bhfield' },
  { Icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/bhfield/' },
  { Icon: TiktokIcon, label: 'TikTok', href: 'https://www.tiktok.com/@bhfield' },
  { Icon: Youtube, label: 'YouTube', href: 'https://youtube.com/@bhfieldinc' },
] as const;

const EXPLORE_LINKS = [
  { key: 'about', to: '/about' },
  { key: 'team', to: '/team' },
  { key: 'services', to: '/services' },
  { key: 'locations', to: '/locations' },
  { key: 'faq', to: '/faq' },
  { key: 'careers', to: '/careers' },
  { key: 'resources', to: '/resources' },
  { key: 'getStarted', to: '/get-started' },
  { key: 'contact', to: '/contact' },
] as const;

const LEGAL_LINKS = [
  { key: 'privacy', to: '/privacy-policy' },
  { key: 'disclaimer', to: '/disclaimer' },
  { key: 'disclosures', to: '/legal-disclosures' },
  { key: 'clientsRights', to: '/clients-rights-and-responsibilities' },
  { key: 'appPrivacy', to: '/privacy-policy#app' },
] as const;

const footerLinkClass =
  'self-start rounded-sm text-sm text-foreground transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export function SiteFooter() {
  const { t, i18n } = useTranslation();
  const { t: tc } = useTranslation('cookies');
  const consent = useCookieConsent();
  const { states, sites } = useLayoutData();

  return (
    <footer className="border-t border-border bg-background-alt px-4 pt-12 pb-8 md:px-6 md:pt-16 md:pb-10 lg:px-24 lg:pt-20 lg:pb-12">
      <div className="mx-auto flex max-w-[1248px] flex-col gap-12 md:gap-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_auto] lg:gap-16">
          <div className="flex flex-col gap-6">
            <Link
              to="/"
              aria-label={t('nav.homeAria')}
              className="self-start rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <img src="/bhfield-logo.svg" alt="" width={94} height={40} className="block h-[40px] w-auto" />
            </Link>
            <p className="max-w-[320px] text-sm leading-[22px] text-foreground-soft">{t('footer.tagline')}</p>
            <div className="flex flex-col gap-2 pt-2">
              <a href="tel:+18774381265" className={footerLinkClass}>
                {t('footer.phone')}
              </a>
              <a href="mailto:info@bhfield.com" className={footerLinkClass}>
                {t('footer.email')}
              </a>
              {/* Fax is display-only — no tel: link, since dialling it from a phone is never the intent. */}
              <p className="text-sm text-foreground-soft">Fax {t('footer.fax')}</p>
            </div>
          </div>
          <div className="flex flex-col gap-12 sm:flex-row sm:flex-wrap sm:gap-16 lg:gap-20">
            {/* Every state links to its page and every site's address is printed in
                full: the same name, address and phone wherever the business appears
                is a local-search signal, and this is the one place every page shares. */}
            <div className="flex flex-col gap-4">
              <p className="font-mono text-[11px] tracking-[1.1px] text-muted-foreground uppercase">
                {t('footer.locations')}
              </p>
              {states.map((state) => {
                const stateSites = sites.filter((site) => site.state === state.code);
                return (
                  <div key={state.code} className="flex flex-col gap-1">
                    <Link to={statePath(state.slug)} className={footerLinkClass}>
                      {stateName(state, i18n.language)}
                      {!isLive(state) && (
                        <span className="ml-2 font-mono text-[10px] tracking-[0.6px] text-muted-foreground uppercase">
                          {t('footer.comingSoon')}
                        </span>
                      )}
                    </Link>
                    {stateSites.map((site) => (
                      <address
                        key={site.slug}
                        className="max-w-[240px] text-[13px] leading-5 text-foreground-soft not-italic"
                      >
                        {site.address_line1}
                        {site.address_line2 ? `, ${site.address_line2}` : ''}, {site.city}, {site.state}{' '}
                        {site.postal_code}
                      </address>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-x-10 gap-y-4 self-start">
              <p className="col-span-2 font-mono text-[11px] tracking-[1.1px] text-muted-foreground uppercase">
                {t('footer.explore')}
              </p>
              {EXPLORE_LINKS.map(({ key, to }) => (
                <Link key={key} to={to} className={footerLinkClass}>
                  {t(`footer.links.${key}`)}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              <p className="font-mono text-[11px] tracking-[1.1px] text-muted-foreground uppercase">
                {t('footer.legal')}
              </p>
              {LEGAL_LINKS.map(({ key, to }) => (
                <Link key={key} to={to} className={footerLinkClass}>
                  {t(`footer.legalLinks.${key}`)}
                </Link>
              ))}
              <a
                href="https://hotline.bhfield.com"
                target="_blank"
                rel="noopener noreferrer"
                className={footerLinkClass}
              >
                {t('footer.legalLinks.hotline')}
              </a>
              {/* Reopens the preferences dialog. Rendered only where the provider
                  is mounted and a decision is therefore possible. */}
              {consent && (
                <button
                  type="button"
                  onClick={(event) => consent.openSettings(event.currentTarget)}
                  className={`${footerLinkClass} text-left`}
                >
                  {tc('actions.footerLink')}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="h-px w-full bg-border" />
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[13px] text-muted-foreground">{t('footer.copyright')}</p>
          <div className="flex gap-3">
            {SOCIAL_LINKS.map(({ Icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex size-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Icon className="size-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
